import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Alert } from "@mui/lab";
import { Box, Button, Paper, Typography, styled } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getToken } from "auth/helpers/authApi";
import { useAuthContext } from "context/GameAuthContext";
import { useRoomTemplateLoginPageContext } from "context/RoomTemplateContext";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { RoomStatus } from "types";
import { apiClient } from "utils/apiClient";
import { getInvertColor } from "utils/themeUtils";
import { BottomLeftCloud } from "../../components/svgBackground/BottomLeftCloud";
import { GameTips } from "../../components/tips/GameTips";
import { useGameContext } from "../../context/GameContext";
import { useAudio } from "../hooks/useAudio";
import { ConnectionLine } from "./ConnectionLine/ConnectionLine";
import { TopRightBanner } from "./TopRightBanner/TopRightBanner";
const ExitIcon = React.lazy(() => import("@mui/icons-material/ExitToApp"));

const connectionLineSettings = [
	{
		top: 20,
		left: -60,
	},
	{
		top: 185,
		left: -58,
	},
	{
		top: 300,
		left: -62,
	},
	{
		top: 418,
		left: -57,
	},
	{
		top: 485,
		left: -58,
	},
];

const userSettings = [
	{
		top: 10,
		left: -60,
	},
	{
		top: 180,
		left: -60,
	},
	{
		top: 350,
		left: -60,
	},
	{
		top: 520,
		left: -60,
	},
	{
		top: 680,
		left: -60,
	},
];
const forceStart = async (roomId) => {
	await apiClient(`admin/rooms/start/${roomId}`, { token: getToken(), data: {} });
};
const LobbyScreen: React.FC = () => {
	const intl = useIntl();
	const { activeUsers, room } = useGameContext();
	const { value: roomTemplate } = useRoomTemplateLoginPageContext();
	const users = room!.users;
	const classes = useStyles();
	const { logout } = useAuthContext();
	useAudio("https://riddlestory-bucket1.s3.eu-central-1.amazonaws.com/assets/RiddleStory_Theme.wav", 0.5, true);

	const lobbyContent = roomTemplate?.lobbyContent;

	const waitingMessage = lobbyContent?.waitingMessage
		? intl.formatMessage({
			id: lobbyContent?.waitingMessage,
			defaultMessage: intl.formatMessage({ id: "WAITING_ROOM.connecting_card.waiting_to_connect" }),
		})
		: intl.formatMessage({ id: "WAITING_ROOM.connecting_card.waiting_to_connect" });

	const tipsMessage = lobbyContent?.tips
		? intl.formatMessage({
			id: lobbyContent?.tips,
			defaultMessage: intl.formatMessage({ id: "WAITING_ROOM.tips.text" }),
		})
		: intl.formatMessage({ id: "WAITING_ROOM.tips.text" });

	return (
		<>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					marginLeft: "-10px",
					transform: "scale(80%)",
				}}
			>
				<div style={{ flex: 1 }}>
					<div style={{ display: "flex", flexDirection: "row", alignItems: "center", flex: 1 }}>
						<ConnectingCard
							message={waitingMessage}
							theme={room?.organization?.theme}
							roomId={room?.roomId}
							featureConfig={roomTemplate?.featureConfig || {}}
							status={room?.status}
							isForceStarted={room?.isForceStarted}
						/>
						<div style={{ position: "relative", width: 531, height: 747 }}>
							{users.map((user, i) => (
								<div
									key={user._id}
									style={{
										position: "absolute",
										width: "100%",
										top: connectionLineSettings[i].top,
										left: connectionLineSettings[i].left,
									}}
								>
									<ConnectionLine
										isOnline={activeUsers.some((au) => au.username === user.username)}
										nr={i}
									/>
								</div>
							))}
						</div>
						<div style={{ position: "relative", height: 747 }}>
							{users.map((user, i) => (
								<div
									key={user.username}
									style={{
										position: "absolute",
										top: userSettings[i].top,
										left: userSettings[i].left,
									}}
								>
									<UserCard
										name={user.name}
										isOnline={activeUsers.some((au) => au.username === user.username)}
									/>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
			<BottomLeftCloud color="#EFEFEFE6" />
			<TopRightBanner />
			<Box position="fixed" top="30vh" right="2vw" width="15vw" zIndex={-1}>
				<GameTips tipsTexts={[tipsMessage]} />
			</Box>
			<Button id="button-logout" className={`${classes.logoutButton}`} onClick={logout}>
				{intl.formatMessage({ id: "USERDASHBOARD.header.logout" })} &nbsp;
				<ExitIcon className={`${classes.logoutIcon}`} />
			</Button>
		</>
	);
};

const MuiAlert = styled(Alert)({
	boxShadow: "0px 20px 40px #00000029",
	fontSize: 18,
	alignItems: "center",
	width: 380,
});

const UserCard: React.FC<{ name: string; isOnline?: boolean }> = ({ name, isOnline }) => {
	const severity = isOnline ? "success" : "warning";
	const locKey = isOnline ? "WAITING_ROOM.user.connected" : "WAITING_ROOM.user.not_connected";

	return (
		<MuiAlert variant="filled" elevation={6} severity={severity}>
			<FormattedMessage id={locKey} values={{ name, bold: (str) => <b>{str}</b> }} />
		</MuiAlert>
	);
};

const ConnectingCard = ({
	message,
	theme,
	roomId,
	featureConfig,
	status,
	isForceStarted,
}: {
	message?: string;
	theme: any;
	roomId: any;
	featureConfig: any;
	status: string | undefined;
	isForceStarted: boolean | undefined;
}) => {
	const intl = useIntl();
	return (
		<Paper
			style={{
				width: 445,
				height: 367,
				padding: 32,
				paddingRight: 72,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				flexDirection: "column",
				boxShadow: "0px 15px 40px #77777729",
			}}
		>
			<AccessTimeIcon style={{ width: 115, height: 115 }} />
			<div style={{ margin: "16px 0" }} />
			<Typography variant="h5" style={{ fontWeight: "bold", fontSize: 30 }}>
				{message || "Even wachten tot het team compleet is."}
			</Typography>
			{featureConfig?.forceStart && status === RoomStatus.READY && !isForceStarted && (
				<Button
					onClick={() => {
						if (window.confirm(intl.formatMessage({ id: "WAITING_ROOM.forceStart.confirm-box" }))) {
							forceStart(roomId);
						} else {
						}
					}}
					style={{
						padding: "20px 20px",
						color: `white`,
						backgroundColor: `${theme.color.primary.main}`,
						fontWeight: "bold",
						marginTop: 20,
						textTransform: "none",
					}}
				>
					{intl.formatMessage({ id: "WAITING_ROOM.forceStart.button" })}
				</Button>
			)}
		</Paper>
	);
};

export default LobbyScreen;

const useStyles = makeStyles((theme) => ({
	logoutButton: {
		position: "absolute",
		top: 16,
		right: 20,
		backgroundColor: "transparent",
		border: `2px solid ${getInvertColor(theme.palette.primary.main)}`,
		color: getInvertColor(theme.palette.primary.main),
		height: "fit-content",
		fontSize: "1rem",
		fontWeight: "bold",
		padding: "0 10px",
		textTransform: "none",
		"&:hover": {
			color: "black",
			backgroundColor: "white",
		},
	},
	logoutIcon: {
		width: 17.26,
		heigth: 14.85,
	},
}));
